import { Download, Plus, Receipt, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  useCreate,
  useDelete,
  useGetList,
  useNotify,
  useRefresh,
} from "ra-core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { EmptyState } from "../data-display/EmptyState";
import { SectionCard } from "../data-display/SectionCard";
import { ListPageHeader } from "../list/ListPageHeader";
import { RecordFiles } from "../files/RecordFiles";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

const inputClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

export const EXPENSE_CATEGORIES = [
  "materials",
  "subcontractor",
  "fuel",
  "vehicle",
  "tools",
  "permits",
  "insurance",
  "marketing",
  "software",
  "office",
  "other",
] as const;

interface Expense {
  id: string;
  vendor: string;
  category: string;
  spent_on: string;
  amount_cents: number;
  tax_cents: number;
  total_cents: number;
  job_id: string | null;
  billable: boolean;
  notes: string | null;
}

const money = (cents: number) =>
  (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

/** Dollars in the input, cents in the database, converted in one place. */
const toCents = (dollars: string) => Math.round(Number(dollars || 0) * 100);

const label = (category: string) =>
  category.charAt(0).toUpperCase() + category.slice(1);

/**
 * Expense tracking: vendor, category, amount and tax, optional job link for
 * job costing, and a receipt through the standard record-files uploader.
 *
 * Amount is entered in dollars and stored in integer cents; tax is kept
 * apart from the amount because it is reclaimable in most trades.
 */
export const ExpensesPage = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [create, { isPending }] = useCreate();
  const [deleteOne] = useDelete();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [vendor, setVendor] = useState("");
  const [category, setCategory] = useState<string>("materials");
  const [spentOn, setSpentOn] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState("");
  const [tax, setTax] = useState("");
  const [jobId, setJobId] = useState("");
  const [billable, setBillable] = useState(false);
  const [monthFilter, setMonthFilter] = useState("");

  const { data: expenses } = useGetList<Expense>("expenses", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "spent_on", order: "DESC" },
  });
  const { data: jobs } = useGetList<{ id: string; title: string }>("jobs", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "created_at", order: "DESC" },
  });

  const rows = (expenses ?? []).filter(
    (e) => !monthFilter || e.spent_on.startsWith(monthFilter),
  );
  const total = rows.reduce((sum, e) => sum + e.total_cents, 0);
  const taxTotal = rows.reduce((sum, e) => sum + e.tax_cents, 0);

  const byCategory = rows.reduce<Record<string, number>>((acc, e) => {
    acc[e.category] = (acc[e.category] ?? 0) + e.total_cents;
    return acc;
  }, {});

  const jobTitle = (id: string | null) =>
    (jobs ?? []).find((j) => j.id === id)?.title ?? "";

  const add = () => {
    if (!vendor.trim()) {
      notify("Who was paid?", { type: "warning" });
      return;
    }
    if (toCents(amount) <= 0) {
      notify("Enter an amount greater than zero", { type: "warning" });
      return;
    }
    create(
      "expenses",
      {
        data: {
          workspace_id: getActiveWorkspaceId(),
          vendor: vendor.trim(),
          category,
          spent_on: spentOn,
          amount_cents: toCents(amount),
          tax_cents: toCents(tax),
          job_id: jobId || null,
          billable,
        },
      },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          setVendor("");
          setAmount("");
          setTax("");
          setJobId("");
          setBillable(false);
          refresh();
        },
        onError: (error: unknown) =>
          notify(
            error instanceof Error
              ? error.message
              : "Could not save the expense",
            { type: "error" },
          ),
      },
    );
  };

  /** Same shape as the finance exports elsewhere: one row per expense. */
  const exportCsv = () => {
    const header = [
      "Date",
      "Vendor",
      "Category",
      "Job",
      "Billable",
      "Amount",
      "Tax",
      "Total",
    ];
    const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;
    const lines = rows.map((e) =>
      [
        e.spent_on,
        escape(e.vendor),
        e.category,
        escape(jobTitle(e.job_id)),
        e.billable ? "yes" : "no",
        (e.amount_cents / 100).toFixed(2),
        (e.tax_cents / 100).toFixed(2),
        (e.total_cents / 100).toFixed(2),
      ].join(","),
    );
    const csv = [header.join(","), ...lines].join("\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `gravun-expenses${monthFilter ? `-${monthFilter}` : ""}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-4">
      <ListPageHeader
        title="Expenses"
        subtitle="What the business spent, by vendor, category and job"
        actions={
          <div className="flex items-center gap-2">
            <input
              type="month"
              className={cn(inputClass, "w-40")}
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
            />
            <Button variant="outline" size="sm" onClick={exportCsv}>
              <Download className="mr-1 size-3.5" />
              Export
            </Button>
          </div>
        }
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <SectionCard title="Total spend">
          <p className="px-1 text-2xl font-semibold tabular-nums">
            {money(total)}
          </p>
          <p className="px-1 text-[11.5px] text-muted-foreground">
            {rows.length} expense{rows.length === 1 ? "" : "s"}
            {monthFilter ? ` in ${monthFilter}` : " recorded"}
          </p>
        </SectionCard>
        <SectionCard title="Tax paid">
          <p className="px-1 text-2xl font-semibold tabular-nums">
            {money(taxTotal)}
          </p>
          <p className="px-1 text-[11.5px] text-muted-foreground">
            Kept apart from the amount because it is usually reclaimable.
          </p>
        </SectionCard>
        <SectionCard title="Top categories">
          <div className="flex flex-col gap-0.5 px-1">
            {Object.entries(byCategory)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 4)
              .map(([cat, cents]) => (
                <div
                  key={cat}
                  className="flex items-center justify-between text-[12.5px]"
                >
                  <span>{label(cat)}</span>
                  <span className="tabular-nums">{money(cents)}</span>
                </div>
              ))}
            {rows.length === 0 ? (
              <span className="text-[12.5px] text-muted-foreground">
                Nothing recorded yet.
              </span>
            ) : null}
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Record an expense">
        <div className="flex flex-wrap items-end gap-2 px-1">
          <label className="flex flex-col gap-1 text-[12px] font-medium">
            Vendor
            <input
              className={cn(inputClass, "w-44")}
              placeholder="Ferguson Supply"
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-medium">
            Category
            <select
              className={cn(inputClass, "w-36")}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {EXPENSE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {label(c)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-medium">
            Date
            <input
              type="date"
              className={cn(inputClass, "w-36")}
              value={spentOn}
              onChange={(e) => setSpentOn(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-medium">
            Amount
            <input
              type="number"
              step="0.01"
              min="0"
              className={cn(inputClass, "w-28")}
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-medium">
            Tax
            <input
              type="number"
              step="0.01"
              min="0"
              className={cn(inputClass, "w-24")}
              placeholder="0.00"
              value={tax}
              onChange={(e) => setTax(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px] font-medium">
            Job (optional)
            <select
              className={cn(inputClass, "w-44")}
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
            >
              <option value="">No job</option>
              {(jobs ?? []).map((j) => (
                <option key={j.id} value={j.id}>
                  {j.title}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1 pb-2 text-[12.5px]">
            <input
              type="checkbox"
              checked={billable}
              onChange={(e) => setBillable(e.target.checked)}
            />
            Billable
          </label>
          <Button size="sm" disabled={isPending} onClick={add}>
            <Plus className="mr-1 size-3.5" />
            Add
          </Button>
        </div>
      </SectionCard>

      <SectionCard title="Recorded expenses">
        {rows.length === 0 ? (
          <div className="py-8">
            <EmptyState
              icon={Receipt}
              title="No expenses yet"
              description="Add what the business spends to see job costs and tax totals."
            />
          </div>
        ) : (
          <div className="flex flex-col">
            {rows.map((expense) => (
              <div
                key={expense.id}
                className="flex flex-col gap-1 border-b border-border-subtle py-2 last:border-0"
              >
                <div className="flex flex-wrap items-center gap-2 text-[13px]">
                  <span className="w-24 shrink-0 text-muted-foreground">
                    {expense.spent_on}
                  </span>
                  <span className="font-medium">{expense.vendor}</span>
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[11px]">
                    {label(expense.category)}
                  </span>
                  {expense.job_id ? (
                    <span className="text-[11.5px] text-muted-foreground">
                      {jobTitle(expense.job_id)}
                    </span>
                  ) : null}
                  {expense.billable ? (
                    <span className="text-[11.5px] text-success">Billable</span>
                  ) : null}
                  <span className="ml-auto tabular-nums">
                    {money(expense.total_cents)}
                  </span>
                  <button
                    type="button"
                    className="text-[11.5px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                    onClick={() =>
                      setSelectedId(
                        selectedId === expense.id ? null : expense.id,
                      )
                    }
                  >
                    Receipt
                  </button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 text-muted-foreground hover:text-destructive"
                    onClick={() =>
                      deleteOne(
                        "expenses",
                        { id: expense.id, previousData: expense },
                        {
                          mutationMode: "pessimistic",
                          onSuccess: () => refresh(),
                        },
                      )
                    }
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
                {selectedId === expense.id ? (
                  <div className="pl-24">
                    <RecordFiles
                      subject={{ expense_id: expense.id }}
                      hideHeader
                    />
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
};
